/**
 * Text utilities for safely rendering user-provided content.
 * All functions return plain strings — the caller is expected to render
 * them via Svelte's `{value}` interpolation (which HTML-escapes automatically).
 */

const MAX_URL_LENGTH = 2048;

/**
 * Escapes HTML-unsafe characters. Callers that must inject into raw HTML
 * (should be rare — prefer Svelte's default text interpolation) can use this.
 */
export function escapeHtml(input: string): string {
  return input
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/** Text/link segment produced by `autolink`. Subset of `InlineSegment`. */
export type TextSegment =
  | { kind: 'text'; value: string }
  | { kind: 'link'; value: string; href: string };

// Extremely conservative URL matcher — http(s) + trailing punctuation stripped.
const URL_RE = /\b(https?:\/\/[^\s<>"]+)/gi;

/**
 * Splits a plain-text string into text + autolink segments, never creating
 * raw HTML. The caller renders segments with Svelte's text interpolation.
 */
export function autolink(input: string): TextSegment[] {
  if (!input) return [];
  const segments: TextSegment[] = [];
  let lastIndex = 0;

  for (const match of input.matchAll(URL_RE)) {
    const index = match.index ?? 0;
    let url = match[0];

    const trailing = url.match(/[),.;:!?]+$/);
    let trailingPunct = '';
    if (trailing) {
      trailingPunct = trailing[0];
      url = url.slice(0, -trailingPunct.length);
    }

    if (url.length > MAX_URL_LENGTH) continue;

    if (index > lastIndex) {
      segments.push({ kind: 'text', value: input.slice(lastIndex, index) });
    }

    if (isSafeUrl(url)) {
      segments.push({ kind: 'link', value: url, href: url });
    } else {
      segments.push({ kind: 'text', value: url });
    }

    if (trailingPunct) {
      segments.push({ kind: 'text', value: trailingPunct });
    }

    lastIndex = index + match[0].length;
  }

  if (lastIndex < input.length) {
    segments.push({ kind: 'text', value: input.slice(lastIndex) });
  }

  return segments;
}

function isSafeUrl(url: string): boolean {
  try {
    const parsed = new URL(url);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:';
  } catch {
    return false;
  }
}

/**
 * Collapses excessive whitespace and trims for search comparisons.
 */
export function normaliseForSearch(input: string): string {
  return input.toLowerCase().replace(/\s+/g, ' ').trim();
}

// ---------- Minimal markdown for chat messages ----------

export type InlineSegment =
  | { kind: 'text'; value: string }
  | { kind: 'link'; value: string; href: string }
  | { kind: 'bold'; inlines: InlineSegment[] }
  | { kind: 'italic'; inlines: InlineSegment[] }
  | { kind: 'code'; value: string };

export type MarkdownBlock =
  | { kind: 'paragraph'; inlines: InlineSegment[] }
  | { kind: 'list'; ordered: boolean; items: InlineSegment[][] }
  | { kind: 'code-block'; value: string };

/**
 * Parses a safe subset of Markdown for chat bubbles. Output is a tree of
 * segment objects that Svelte renders with text interpolation, so the
 * caller never handles raw HTML. Supported syntax:
 *
 *   - Paragraphs separated by blank lines
 *   - Ordered lists (`1. `) and unordered lists (`- ` / `* `)
 *   - Fenced code blocks (``` )
 *   - Inline bold (`**x**`), italic (`*x*` / `_x_`), code (`` `x` ``)
 *   - Bare URL autolinking (via `autolink`)
 *
 * Not supported: markdown `[text](url)` links (URLs must be bare to keep
 * the href-sanitisation path narrow), headings, blockquotes, tables,
 * HTML passthrough. Scope is intentionally tight for chat UX.
 */
export function parseMarkdown(input: string): MarkdownBlock[] {
  if (!input) return [];
  const lines = input.replace(/\r\n?/g, '\n').split('\n');
  const blocks: MarkdownBlock[] = [];

  let i = 0;
  while (i < lines.length) {
    const line = lines[i]!;

    if (line.trim().length === 0) {
      i += 1;
      continue;
    }

    if (line.startsWith('```')) {
      const body: string[] = [];
      i += 1;
      while (i < lines.length && !lines[i]!.startsWith('```')) {
        body.push(lines[i]!);
        i += 1;
      }
      if (i < lines.length) i += 1; // skip closing fence
      blocks.push({ kind: 'code-block', value: body.join('\n') });
      continue;
    }

    const orderedMatch = line.match(/^\s*(\d+)\.\s+(.*)$/);
    const bulletMatch = line.match(/^\s*[-*]\s+(.*)$/);
    if (orderedMatch || bulletMatch) {
      const ordered = orderedMatch !== null;
      const items: InlineSegment[][] = [];
      while (i < lines.length) {
        const m = ordered
          ? lines[i]!.match(/^\s*\d+\.\s+(.*)$/)
          : lines[i]!.match(/^\s*[-*]\s+(.*)$/);
        if (!m) break;
        items.push(parseInlines(m[1] ?? ''));
        i += 1;
      }
      blocks.push({ kind: 'list', ordered, items });
      continue;
    }

    const paraLines: string[] = [line];
    i += 1;
    while (
      i < lines.length &&
      lines[i]!.trim().length > 0 &&
      !lines[i]!.startsWith('```') &&
      !/^\s*(\d+\.|-|\*)\s+/.test(lines[i]!)
    ) {
      paraLines.push(lines[i]!);
      i += 1;
    }
    blocks.push({
      kind: 'paragraph',
      inlines: parseInlines(paraLines.join(' ')),
    });
  }

  return blocks;
}

/** Tokenise a single line into inline segments. Safe — no raw HTML. */
function parseInlines(input: string): InlineSegment[] {
  if (!input) return [];
  const out: InlineSegment[] = [];
  // Precedence: inline code (backtick) → bold (**) → italic (* or _) → autolink → text.
  const tokenRe = /(`[^`\n]+`)|(\*\*[^*\n]+\*\*)|(\*[^*\n]+\*|_[^_\n]+_)/g;
  let last = 0;
  for (const m of input.matchAll(tokenRe)) {
    const idx = m.index ?? 0;
    if (idx > last) out.push(...autolink(input.slice(last, idx)));
    const token = m[0];
    if (token.startsWith('`')) {
      out.push({ kind: 'code', value: token.slice(1, -1) });
    } else if (token.startsWith('**')) {
      out.push({ kind: 'bold', inlines: parseInlines(token.slice(2, -2)) });
    } else {
      out.push({ kind: 'italic', inlines: parseInlines(token.slice(1, -1)) });
    }
    last = idx + token.length;
  }
  if (last < input.length) out.push(...autolink(input.slice(last)));
  return out;
}
